import clsx from 'clsx';
import { forwardRef, type ReactNode, type ReactElement } from 'react';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { actionsSlotCss } from '../EmptyState.sty.js';

//#region Typings
/**
 * @public
 */
export interface EmptyStateActionsProps
  extends WithChildren,
    StylingProps,
    DataTestId {}
//#endregion Typings

/**
 * Typeguard function that will check if a ReactNode is a EmptyState.Actions
 * @internal
 */
export function isEmptyStateActions(
  child: ReactNode,
): child is ReactElement<EmptyStateActionsProps> {
  return isElement(child) && child.type === Actions;
}

/**
 * The actions slot of the EmptyState component.
 * @public
 */
export const Actions = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateActionsProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;

  return (
    <Flex
      ref={forwardedRef}
      flexDirection="row"
      alignItems="center"
      gap={8}
      className={clsx([actionsSlotCss, consumerClassName])}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      {children}
    </Flex>
  );
});

(Actions as typeof Actions & { displayName: string }).displayName =
  'EmptyState.Actions';
